import {Three as THREE} from "#three-texture-streaming/three-exports"
import {RefCountableBase} from "@cm/utils"
import {TextureVirtualThreeNode} from "#three-texture-streaming/texture-virtual-three-node"
import {TRACE_TILED_TEXTURES} from "#three-texture-streaming/texture-tile-constants"
import type {ThreeTiledTexture} from "#three-texture-streaming/three-tiled-texture"
import {sceneLogger as logger} from "#three-texture-streaming/scene-logger"

const TRACE = TRACE_TILED_TEXTURES

export enum RenderMode {
    Normal = 0,
    MipLevelVisualization = 1,
    TileVisualization = 2,
    TiledTextureIdVisualization = 3,
    IntraTileUvVisualization = 4,
    MipShiftVisualization = 5,
    UvVisualization = 6,
    TileIndexFVisualization = 7,
    MipLevelFracVisualization = 8,
}

export class ThreeTiledTextureInstance extends RefCountableBase {
    constructor(
        readonly tiledTexture: ThreeTiledTexture,
        readonly uvNode: THREE.Node<"vec2"> | THREE.Node<"vec3">,
        readonly minMipLevel = 0,
    ) {
        super({
            disposeFn: () => this.dispose(),
        })
        this.tiledTexture.addRef()
        const textureSize = new THREE.Vector2(this.tiledTexture.imageTilesDescriptor.imageWidth, this.tiledTexture.imageTilesDescriptor.imageHeight)
        this._textureNode = new TextureVirtualThreeNode(
            this.tiledTexture.atlasTextureNode,
            this.tiledTexture.tileLookupTexture,
            textureSize,
            uvNode,
            tiledTexture.isSrgb,
            tiledTexture.atlasFormat,
            tiledTexture.getComputeMipLevelTsl(),
            minMipLevel,
            tiledTexture.getMaxPixelsPerTexel(),
            tiledTexture.id,
            () => this.tiledTexture.flushTileLookupUpdates(),
            ThreeTiledTextureInstance._renderMode,
        )
        ThreeTiledTextureInstance.instances.add(this)
    }

    protected dispose(): void {
        if (TRACE) {
            logger.log("ThreeTiledTextureInstance: Disposing", this)
        }
        ThreeTiledTextureInstance.instances.delete(this)
        if (!this._textureNode) {
            throw new Error("ThreeTiledTextureInstance: already disposed")
        }
        this._textureNode = null
        this.tiledTexture.release()
    }

    get textureNode(): THREE.Node<"vec4"> {
        if (!this._textureNode) {
            throw new Error("ThreeTiledTextureInstance: textureNode has been disposed")
        }
        return this._textureNode
    }

    private _textureNode: TextureVirtualThreeNode | null

    // DEBUGGING STUFF

    static set renderMode(value: RenderMode) {
        ThreeTiledTextureInstance.instances.forEach((instance) => {
            const renderMode = instance._textureNode?.renderMode
            if (renderMode) {
                renderMode.value = value
            }
        })
    }

    static get renderMode(): number {
        return ThreeTiledTextureInstance._renderMode
    }

    static _renderMode = RenderMode.Normal

    static incMinMipLevel(): void {
        ThreeTiledTextureInstance.instances.forEach((instance) => {
            const mipMipLevel = instance._textureNode?.minMipLevel
            if (mipMipLevel) {
                mipMipLevel.value = Math.min(mipMipLevel.value + 1, instance.tiledTexture.numMipLevels - 1)
            }
        })
    }

    static decMinMipLevel(): void {
        ThreeTiledTextureInstance.instances.forEach((instance) => {
            const mipMipLevel = instance._textureNode?.minMipLevel
            if (mipMipLevel) {
                mipMipLevel.value = Math.max(mipMipLevel.value - 1, 0)
            }
        })
    }

    static instances = new Set<ThreeTiledTextureInstance>()
}
